import { type Directive, nextTick } from 'vue';

type ClickOutsideHandler = (event: MouseEvent, el: HTMLElement) => void;
// The directive stashes its own listener on the element so `unmounted` can
// remove the same reference. Reflect that in the el type rather than casting
// to any.
type ClickOutsideEl = HTMLElement & { event?: (event: MouseEvent) => void };

/**
 * Usage:
 *  <div v-click-outside="hide">
 */
export const directiveClickOutside: Directive<ClickOutsideEl, ClickOutsideHandler> = {
  beforeMount(el, binding) {
    el.event = (event) => {
      // Check that click was outside the el and his children
      if (el !== event.target && !el.contains(event.target as Node)) {
        // and if it is, call method provided in attribute value
        binding.value(event, el);
      }
    };
    document.body.addEventListener('click', el.event);
  },
  unmounted(el) {
    if (el.event) document.body.removeEventListener('click', el.event);
  },
};

// Excludes elements that match the broad selector but cannot actually be
// focused / Tab-reached: disabled form controls, hidden inputs, and explicitly
// removed-from-tab-order tabindex="-1". Without these, v-focus on a wrapper
// containing a disabled <button> would silently no-op or focus an unreachable
// element.
const FOCUSABLE_SELECTOR =
  'input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])';

/**
 * v-focus: focuses the bound element (or its first focusable descendant)
 * when mounted.
 *
 * Usage:
 *   <input v-focus>                   // implicit on — focuses after mount
 *   <input v-focus="true">            // explicit on
 *   <input v-focus="false">           // explicit off — no focus
 *   <input v-focus="someReactiveBool"> // conditional
 *   <KaInput v-focus="props.autofocus" /> // works on components too (see "descend" below)
 *   <Cmp v-focus.visible="cond" />    // also force the :focus-visible ring + scrollIntoView
 *   <Cmp v-focus.force="cond" />      // also skip the same-form steal guard (see #3)
 *
 * Gotchas / how the API is supposed to behave:
 *
 * 1. Binding semantics — treat the binding as a plain boolean.
 *    - `undefined` (no binding) is a special case: "yes, focus" (so naked
 *      `v-focus` on an input keeps its historical always-on behavior).
 *    - Any OTHER falsy value — `false`, `null`, `0`, `''` — is an explicit
 *      opt-out and skips. This matches `v-if` / `v-show` semantics: a
 *      nullable or numeric binding that resolves to falsy means "off".
 *    - Truthy values focus.
 *    - Rule in code: `if (binding.value !== undefined && !binding.value) return;`
 *
 * 2. Timing — the focus call runs inside `nextTick`, NOT synchronously
 *    inside `mounted`. Rationale: conditional children / slots may not be
 *    patched into the DOM yet at the mounted hook; nextTick lets Vue finish
 *    patching first. A previous implementation used `setTimeout(100)` for
 *    the same reason, but 100 ms is a race window against follow-up user
 *    interactions (a click that opens a dropdown, etc.), which can end up
 *    stealing the focus mid-flight. `nextTick` is deterministic, runs in
 *    the same tick, and closes that race.
 *
 *    The nextTick callback bails if `el.isConnected` is false — by the time
 *    it fires the host can have unmounted (animated lists splice rows out
 *    mid-tick), so calling `el.matches(...)` / `.focus()` on a detached
 *    node would either be a silent no-op or focus a zombie reference.
 *
 * 3. Same-form steal guard — if some element inside the nearest `<form>`
 *    ancestor ALREADY has focus by the time nextTick fires, v-focus bails
 *    out. Use case: an add-row button sets `focusNewRow = true`, a new
 *    form mounts with `v-focus="focusNewRow"`, but before nextTick fires
 *    the user has already clicked into a different field in the same form
 *    — we respect that choice instead of yanking focus back. The `.force`
 *    modifier opts out of this guard, for deliberately moving focus onto
 *    content the same action just revealed (the triggering control may
 *    still hold focus inside the form).
 *
 *    Limitation: the check is scoped to the bound element's nearest
 *    `<form>` ancestor. Focus held by UI OUTSIDE that form (dropdown menu
 *    portaled to `<body>`, a modal, a toast) is NOT protected — v-focus
 *    will fire and pull focus back. Authors composing v-focus with a
 *    component that can open portals on the same mount tick should think
 *    about ordering.
 *
 * 4. Descend into focusable — if the directive is bound to a non-focusable
 *    wrapper (e.g. the root `<div>` of a composed primitive like KaInput),
 *    v-focus finds the first focusable descendant and focuses that. This
 *    lets consumers compose `v-focus` with components WITHOUT the component
 *    needing to expose an imperative `focus()` method (avoiding
 *    leaky-abstraction `defineExpose({ focus })` patterns).
 *
 * 5. Fires once, at mount. There is no `updated()` hook — if a reactive
 *    binding flips `false → true` after mount, v-focus will NOT re-focus.
 *    The Vue-idiomatic way to re-trigger is to force a remount via a
 *    changing `:key`, which is often already the case (e.g. forms keyed
 *    by `selectedItem.uuid`).
 *
 * 6. `.visible` modifier — focuses with `{ focusVisible: true }` and scrolls
 *    the target into view. Use when the focus follows a pointer interaction
 *    (which suppresses `:focus-visible`) but should still paint the ring.
 */
export const directiveFocus: Directive<HTMLElement, boolean | undefined> = {
  mounted(el, binding) {
    if (binding.value !== undefined && !binding.value) return;

    const { visible, force } = binding.modifiers;

    nextTick(() => {
      if (!el.isConnected) return;

      // Same-form steal guard, skipped with `.force` (see doc #3).
      const form = el.closest('form');
      if (!force && form && form.contains(document.activeElement)) return;

      const target = el.matches(FOCUSABLE_SELECTOR)
        ? el
        : el.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);

      // `.visible` forces the :focus-visible ring (suppressed after a pointer
      // interaction) and scrolls the target into view.
      target?.focus(visible ? ({ focusVisible: true } as FocusOptions) : undefined);
      if (visible) target?.scrollIntoView({ block: 'nearest' });
    });
  },
};
